import { suggestedCoursePlacementId } from '@cc/schema';
import { useCustom, useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Drawer,
  Form,
  Input,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useState } from 'react';
import { SubmitFooter } from '../../components/SubmitFooter';
import { usePathwayCommand } from '../pathways/usePathwayCommand';

/**
 * E34 · SetPathwayAccessRule — "Backoffice — Course library — Access rule (section)" (cc-homework, chapter
 * "Pathways — Access, capabilities & course review (backoffice, E34)", screen e34e0012-0001-4000-8000-000000000001):
 * CopingCard sets the access rule of each of its courses and programs (the founder and Dmitri, the E33 venue) —
 * free · purchase (with the store product) · plan. The price is set in App Store Connect and the Google Play
 * Console, never here. The library is the Pathways read model of CopingCard's own practice (`courseLibrary`).
 *
 * Behavior (the board): gesture Save rule → SetPathwayAccessRule, i18n backoffice.courses.access.*; format the
 * store product field shows only for `purchase`, i18n backoffice.courses.access.storeProduct; help "Prices are
 * set in the stores; this rule only says who may start the course and how", i18n backoffice.courses.access.help.
 * A refusal (`homework.errors.PATHWAY_*`, in the member's language) is shown verbatim in the section.
 */
type LibraryCourse = {
  pathwayId: string;
  name: string;
  description: string | null;
  visibility: string;
  version: number;
  stepsCount: number;
  retired: boolean;
  accessRule: 'free' | 'purchase' | 'plan' | null;
  storeProductId: string | null;
  adaptyPlacementId: string | null;
  occurredAt: string;
};

const COURSE_FIELDS = [
  'pathwayId',
  'name',
  'description',
  'visibility',
  'version',
  'stepsCount',
  'retired',
  'accessRule',
  'storeProductId',
  'adaptyPlacementId',
  'occurredAt',
];

const RULES = ['free', 'purchase', 'plan'] as const;
type Rule = (typeof RULES)[number];

export const BackofficeCourses = () => {
  const t = useTranslate();
  const { message } = AntdApp.useApp();
  const { run, isLoading: saving } = usePathwayCommand();
  const { data, isFetching, isError, refetch } = useCustom<LibraryCourse[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: { operation: 'courseLibrary', fields: COURSE_FIELDS },
  });
  const courses = data?.data ?? [];
  const [editing, setEditing] = useState<LibraryCourse | null>(null);
  const [rule, setRule] = useState<Rule>('free');
  const [product, setProduct] = useState('');
  const [placement, setPlacement] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const open = (c: LibraryCourse) => {
    setEditing(c);
    setRule(c.accessRule ?? 'free');
    setProduct(c.storeProductId ?? '');
    // Q-cf-3 B: pre-filled with the suggestion (editable) — saving untouched saves it
    setPlacement(c.adaptyPlacementId ?? suggestedCoursePlacementId(c.pathwayId));
    setFailure(null);
  };

  const save = async () => {
    if (!editing) return;
    setFailure(null);
    const result = await run(
      'setPathwayAccessRule',
      {
        pathwayId: editing.pathwayId,
        accessRule: rule,
        storeProductId: rule === 'purchase' ? product.trim() || null : null,
        adaptyPlacementId: rule === 'purchase' ? placement.trim() || null : null,
      },
      t('backoffice.courses.access.failed'),
    );
    if (!result.ok) {
      setFailure(result.message);
      return;
    }
    message.success(t('backoffice.courses.access.saved'));
    setEditing(null);
    await refetch();
  };

  const ruleLine = (c: LibraryCourse) =>
    c.accessRule === 'purchase'
      ? t('backoffice.courses.access.line.purchase', { product: c.storeProductId ?? '—', placement: c.adaptyPlacementId ?? '—' })
      : c.accessRule
      ? t(`backoffice.courses.access.line.${c.accessRule}`)
      : t('backoffice.courses.access.none');

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="backoffice-courses">
      <div>
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          {t('backoffice.courses.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('backoffice.courses.intro')}
        </Typography.Paragraph>
      </div>
      {isError ? <Alert type="error" showIcon message={t('backoffice.courses.loadFailed')} /> : null}
      <Table<LibraryCourse>
        rowKey="pathwayId"
        loading={isFetching}
        dataSource={courses}
        pagination={{ pageSize: 25, hideOnSinglePage: true }}
        locale={{ emptyText: t('backoffice.courses.empty') }}
        onRow={(c) => ({ 'data-testid': `backoffice-course-row-${c.pathwayId}` } as never)}
        columns={[
          {
            title: t('backoffice.courses.name'),
            dataIndex: 'name',
            render: (name: string, c) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>{name}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {c.pathwayId}
                </Typography.Text>
              </Space>
            ),
          },
          { title: t('backoffice.courses.version'), dataIndex: 'version' },
          { title: t('backoffice.courses.steps'), dataIndex: 'stepsCount' },
          {
            title: t('backoffice.courses.rule'),
            key: 'rule',
            render: (_: unknown, c) => (
              <Space size={4} wrap>
                <Tag color={c.accessRule === 'purchase' ? 'gold' : c.accessRule === 'plan' ? 'purple' : 'green'}
                  data-testid={`backoffice-course-rule-${c.pathwayId}`}>
                  {ruleLine(c)}
                </Tag>
                {c.retired ? <Tag>{t('backoffice.courses.retired')}</Tag> : null}
              </Space>
            ),
          },
          {
            title: '',
            key: 'actions',
            render: (_: unknown, c) => (
              <Button onClick={() => open(c)} data-testid={`backoffice-course-set-rule-${c.pathwayId}`}>
                {t('backoffice.courses.setRule')}
              </Button>
            ),
          },
        ]}
      />

      <Drawer
        open={!!editing}
        onClose={() => setEditing(null)}
        width={600}
        destroyOnClose
        title={editing ? t('backoffice.courses.access.title', { name: editing.name }) : ''}
        footer={
          <SubmitFooter
            errors={
              failure ? (
                <Alert type="error" showIcon message={failure} data-testid="backoffice-course-access-error" />
              ) : null
            }
          >
            <Button onClick={() => setEditing(null)} data-testid="backoffice-course-access-cancel">
              {t('backoffice.courses.access.cancel')}
            </Button>
            <Button type="primary" loading={saving} onClick={save} data-testid="backoffice-course-access-save">
              {t('backoffice.courses.access.save')}
            </Button>
          </SubmitFooter>
        }
      >
        {editing ? (
          <div data-testid="backoffice-course-access">
            <Form layout="vertical" onFinish={save}>
              <Form.Item label={t('backoffice.courses.access.question')}>
                <Radio.Group
                  value={rule}
                  onChange={(e) => setRule(e.target.value as Rule)}
                  data-testid="backoffice-course-access-rule"
                >
                  <Space direction="vertical">
                    {RULES.map((r) => (
                      <Radio key={r} value={r} data-testid={`backoffice-course-access-${r}`}>
                        <Typography.Text strong>{t(`backoffice.courses.access.${r}`)}</Typography.Text>
                        {' — '}
                        <Typography.Text type="secondary">{t(`backoffice.courses.access.${r}Help`)}</Typography.Text>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </Form.Item>
              {rule === 'purchase' ? (
                <Form.Item
                  label={t('backoffice.courses.access.storeProduct')}
                  extra={t('backoffice.courses.access.storeProductHelp')}
                >
                  <Input
                    value={product}
                    onChange={(e) => setProduct(e.target.value)}
                    placeholder={t('backoffice.courses.access.storeProductPlaceholder')}
                    maxLength={100}
                    data-testid="backoffice-course-access-product"
                  />
                </Form.Item>
              ) : null}
              {rule === 'purchase' ? (
                <Form.Item
                  label={t('backoffice.courses.access.placement')}
                  extra={t('backoffice.courses.access.placementHelp')}
                >
                  <Input
                    value={placement}
                    onChange={(e) => setPlacement(e.target.value)}
                    maxLength={100}
                    data-testid="backoffice-course-access-placement"
                  />
                </Form.Item>
              ) : null}
              <Typography.Paragraph type="secondary" data-testid="backoffice-course-access-help">
                {t('backoffice.courses.access.help')}
              </Typography.Paragraph>
            </Form>
          </div>
        ) : null}
      </Drawer>
    </Space>
  );
};
